import React, { useEffect, useState } from 'react';
import { Button, Grid, Typography } from '@mui/material';
import _ from 'lodash';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { makeStyles } from '@material-ui/styles';
import { withStyles } from '@material-ui/core/styles';
import Select from '../../select/Select';
import { whenCondition } from './select-options';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import { ExpandMoreOutlined, ArrowUpward, ArrowDownward, FileCopy, Delete } from '@material-ui/icons';
import { ReactComponent as IndentLine } from './IndentLine.svg';
import TieredRuleCondition from './TieredRuleCondition';
import ConfirmationDialog from '../../../../../main/apps/RoleSpecificApps/Admin/user-groups/common/ConfirmationDialog';
import BasicLineItemRate from './BasicLineItemRate';
import { generateTestId } from '../../../../../../utils';
import { tieredRateConditionFormDefault } from './line-item-config-form-utils';

const Accordion = withStyles((theme) => ({
    root: {
        border: 'none',
        boxShadow: 'none',
        minHeight: 56,
        backgroundColor: theme.palette.background.default,
    },
    expanded: {},
}))(MuiAccordion);

const AccordionSummary = withStyles((theme) => ({
    root: {
        borderBottom: 'none',
        paddingLeft: theme.spacing(1),
        minHeight: 56,
        backgroundColor: theme.palette.background.default,
    },
    expanded: {},
}))(MuiAccordionSummary);

const AccordionDetails = withStyles((theme) => ({
    root: {
        padding: 0,
        display: 'block',
        backgroundColor: theme.palette.background.default,
    },
}))(MuiAccordionDetails);

// The form for adding complex conditional rules for line item rates
function TieredLineItemRateRule({ name, index, ruleCount, duplicateRule, swapRule, removeRule, disabledOnEdit = false }) {
    const { watch, control, getValues } = useFormContext();
    const [expanded, setExpanded] = useState(true);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const useStyles = makeStyles((theme) => ({
        conditionalPrefix: {
            backgroundColor: '#EEEEEE',
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(1),
        },
        indentedLine: {
            position: 'absolute',
            left: theme.spacing(2.5),
            top: -theme.spacing(1),
        },
        indentedContainer: {
            paddingLeft: theme.spacing(11),
            paddingTop: theme.spacing(1),
            position: 'relative',
        },
        buttonContainer: {
            paddingTop: theme.spacing(4),
        },
    }));
    const classes = useStyles();

    const { fields, append, remove } = useFieldArray({
        control,
        name: `${name}.conditions`,
    });

    useEffect(() => {
        if (!fields.length) {
            append({});
        }
    });

    const addCondition = () => {
        append(tieredRateConditionFormDefault);
    };

    return (
        <>
            <ConfirmationDialog
                open={showConfirmDialog}
                message="Are you sure you want to delete this rule?"
                onDialogCancelAction={() => setShowConfirmDialog(false)}
                onDialogConfirmAction={removeRule}
                // wrap
            />
            <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
                <AccordionSummary expandIcon={<ExpandMoreOutlined />} data-testid={generateTestId(name, 'accordion-toggle')}>
                    <Typography variant="h6">Rule {index + 1}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container className="mb-24">
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={1}>
                                <Typography textAlign="center" variant="body2" fontWeight="bold" className={classes.conditionalPrefix}>
                                    When
                                </Typography>
                            </Grid>
                            <Grid item xs={2} sx={{ marginLeft: '10px' }}>
                                <Select name={`${name}.when`} props={{ options: whenCondition, required: true, size: 'small', disabled: disabledOnEdit }} />
                            </Grid>
                        </Grid>
                        <Grid container spacing={0} alignItems="center" className={classes.indentedContainer}>
                            <div className={classes.indentedLine}>
                                <IndentLine height={50} width={70} />
                            </div>
                            {fields.map((field, i) => (
                                <TieredRuleCondition key={field.id} name={`${name}.conditions.${i}`} index={i} remove={remove} addCondition={addCondition} disabledOnEdit={disabledOnEdit} />
                            ))}
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid container spacing={1} alignItems="center" sx={{ paddingBottom: '10px' }}>
                            <Grid item xs={2}>
                                <Typography textAlign="center" variant="body2" fontWeight="bold" className={classes.conditionalPrefix}>
                                    Then charge
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container spacing={0} alignItems="center" className={classes.indentedContainer}>
                            <div className={classes.indentedLine}>
                                <IndentLine height={50} width={70} />
                            </div>
                            <BasicLineItemRate name={name} inputSizes="small" disabledOnEdit={disabledOnEdit} />
                        </Grid>
                        <Grid container spacing={2} justifyContent="space-between" className={classes.buttonContainer}>
                            <Grid item xs={10}>
                                <Button variant="outlined" onClick={() => swapRule(index, index - 1)} disabled={index === 0 || disabledOnEdit} data-testid={generateTestId(name, 'move-up-btn')} sx={{ marginRight: '10px' }}>
                                    <ArrowUpward />
                                </Button>
                                <Button variant="outlined" onClick={() => swapRule(index, index + 1)} disabled={index === ruleCount - 1 || disabledOnEdit} data-testid={generateTestId(name, 'move-down-btn')} sx={{ marginRight: '10px' }}>
                                    <ArrowDownward />
                                </Button>
                                <Button variant="outlined" startIcon={<FileCopy />} onClick={() => duplicateRule(index + 1, _.cloneDeep(getValues(name)))} disabled={disabledOnEdit} data-testid={generateTestId(name, 'duplicate-rule-btn')}>
                                    Duplicate
                                </Button>
                            </Grid>
                            <Grid item xs={2}>
                                <Button variant="outlined" color="error" startIcon={<Delete />} onClick={() => setShowConfirmDialog(true)} disabled={index === 0 || disabledOnEdit} data-testid={generateTestId(name, 'delete-btn')}>
                                    Delete
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </>
    );
}

export default TieredLineItemRateRule;
